import { useEffect, useState } from "react";

import { Colors, Icon } from "@blueprintjs/core";
import styled from "@emotion/styled";
import Select from "react-select";

import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { Box, Flex } from "@/components/layout/flexbox";
import { SlotOptions } from "@/components/pieces/coaching/CoachingSettings/timeslots";
import { Clickable } from "@/components/pieces/interaction/Clickable";

import { TimeRange, TimeStop } from "./types";

const SelectBox = styled(Box)`
  min-width: 120px;
  border-radius: 6px;
  border: none;
  color: ${Colors.BLACK};

  .select-time-range {
    [class$="-control"] {
      border: none;
      border-radius: 6px;
      background-color: transparent;
    }
  }
`;

interface TimeRangeSelectorProps {
  loading: boolean;
  slotOptions: SlotOptions;
  timeRange?: string;
  onSubmit?: ({ start, end }: TimeRange) => void;
  onDelete?: () => void;
}

export default function TimeRangeSelector({
  loading = false,
  slotOptions = {},
  timeRange,
  onSubmit,
  onDelete,
}: TimeRangeSelectorProps) {
  const options = Object.entries(slotOptions)
    .map((entry) => ({
      index: entry[1],
      value: entry[0],
      label: entry[0],
    }))
    .sort((p, l) => +p.index - +l.index);

  const [start, end] = timeRange?.split("-") || [];

  const [startTime, setStartTime] = useState<TimeStop>({
    index: slotOptions[start],
    value: start,
    label: start,
  });

  const [endTime, setEndTime] = useState<TimeStop>({
    index: slotOptions[end],
    value: end,
    label: end,
  });

  const [invalidRange, setInvalidRange] = useState(false);

  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(true);
  }, []);

  useEffect(() => {
    if (ready && !!startTime.index && !!endTime.index) {
      const isInvalidRange = !!(startTime.index >= endTime.index);
      setInvalidRange(isInvalidRange);
      if (!isInvalidRange) {
        onSubmit &&
          onSubmit({
            start: startTime.value,
            end: endTime.value,
          });
      }
    }
  }, [startTime, endTime]);

  return (
    <Box mt={2}>
      <Flex alignItems="center">
        <Flex
          alignItems="center"
          justifyContent="space-between"
          backgroundColor={invalidRange ? "#ff807c1f" : Colors.LIGHT_GRAY5}
          className="round-corners"
          p={2}
        >
          <SelectBox>
            <Select<TimeStop>
              placeholder="--:--"
              isDisabled={loading}
              className="select-time-range"
              isLoading={loading}
              value={options.find((o) => o.value === startTime.value)}
              onChange={(newValue) => {
                if (newValue) {
                  setStartTime(newValue);
                }
              }}
              isSearchable={true}
              name="start-time"
              options={options}
              autoFocus={ready}
            />
          </SelectBox>
          <Box px={2}>to</Box>
          <SelectBox>
            <Select<TimeStop>
              placeholder="--:--"
              isDisabled={loading}
              className="select-time-range"
              isLoading={loading}
              value={options.find((o) => o.value === endTime.value)}
              onChange={(newValue) => {
                if (newValue) {
                  setEndTime(newValue);
                }
              }}
              isSearchable={true}
              name="end-time"
              options={options}
            />
          </SelectBox>
        </Flex>
        <Box ml={2}>
          <Tooltip content="Remove">
            <Clickable
              onClick={(e) => {
                e.stopPropagation();
                onDelete && onDelete();
              }}
            >
              <Flex
                width={32}
                height={32}
                alignItems="center"
                justifyContent="center"
              >
                <Icon icon="minus" size={20} />
              </Flex>
            </Clickable>
          </Tooltip>
        </Box>
      </Flex>

      {invalidRange && (
        <Box fontSize="12px" style={{ color: Colors.RED5 }} mt={1}>
          Invalid time range. The start time must be earlier than the end time
        </Box>
      )}
    </Box>
  );
}
